import { Injectable, Injector } from "@angular/core";

import { combineLatest, Observable } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

import { DffCookieConsentService } from "@e-forsyning/web-components/shared/dff-cookie-consent";
import { CookieStorage } from "@dffedb/util";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";

import { deleteServiceProOptionalConsentData } from "./delete-service-pro-optional-consent-data";
import { ServiceProCookieConsentDialog } from "./service-pro-cookie-consent-dialog";
import {
    ServiceProCookieConsentVersionService,
    isServiceProCookieConsentEnabled
} from "./service-pro-cookie-consent-version.service";

export interface ServiceProCookieConsentViewModel {
    enabled: boolean;
    reloading: boolean;
}

@Injectable({ providedIn: "root" })
export class ServiceProCookieConsentSandbox {
    private initialized = false;
    private lastInitKey = "";

    constructor(
        private readonly indstillingerObserver: UmbracoIndstillingerObserver,
        private readonly cookieConsentService: DffCookieConsentService,
        private readonly cookieConsentDialog: ServiceProCookieConsentDialog,
        private readonly versionService: ServiceProCookieConsentVersionService
    ) {}

    public async initializeWithSettings(settings: Parameters<typeof isServiceProCookieConsentEnabled>[0]): Promise<void> {
        const enabled = isServiceProCookieConsentEnabled(settings);
        const version = await this.versionService.resolveConsentVersion(settings);
        const initKey = `${enabled}:${version}`;
        if (this.initialized && initKey === this.lastInitKey) {
            return;
        }
        this.lastInitKey = initKey;
        await this.cookieConsentService.initialize(enabled, version, false);
        this.initialized = true;
    }

    public selectViewModel(): Observable<ServiceProCookieConsentViewModel> {
        return combineLatest([this.indstillingerObserver.valueChanges, this.cookieConsentService.selectCookieConsent()]).pipe(
            map(([settings, cookieConsent]) => ({
                enabled: isServiceProCookieConsentEnabled(settings),
                reloading: cookieConsent.reloading
            })),
            distinctUntilChanged((a, b) => a.enabled === b.enabled && a.reloading === b.reloading)
        );
    }

    public isCookieConsentUpdateNeeded(): boolean {
        return this.cookieConsentService.isCookieConsentUpdateNeeded();
    }

    public showCookieConsentDialog(): void {
        this.cookieConsentDialog.open();
    }

    public renewCookieConsent(): void {
        this.cookieConsentService.renewCookieConsent();
    }

    public async promptIfNeeded(): Promise<void> {
        const settings = await this.indstillingerObserver.getValue();
        if (!isServiceProCookieConsentEnabled(settings)) {
            return;
        }

        await this.initializeWithSettings(settings);

        window.setTimeout(() => {
            if (this.isCookieConsentUpdateNeeded()) {
                this.showCookieConsentDialog();
            } else {
                this.renewCookieConsent();
            }
        }, 0);
    }

    public hasInitialized(): boolean {
        return this.initialized;
    }
}

export function initServiceProCookieConsent(injector: Injector): () => Promise<void> {
    return async () => {
        const sandbox = injector.get(ServiceProCookieConsentSandbox);
        const indstillingerObserver = injector.get(UmbracoIndstillingerObserver);
        const cookieConsentService = injector.get(DffCookieConsentService);
        const cookieStorage = injector.get(CookieStorage);
        let hadFunctionalConsent = cookieConsentService.getCookieConsent().consent.statistics;

        cookieConsentService.selectCookieConsent().subscribe((consent) => {
            const hasFunctionalConsent = consent.consent.statistics;
            if (hadFunctionalConsent && !hasFunctionalConsent) {
                deleteServiceProOptionalConsentData(cookieStorage);
            }
            hadFunctionalConsent = hasFunctionalConsent;
        });

        const bootstrap = async (): Promise<void> => {
            const settings = await indstillingerObserver.getValue();
            await sandbox.initializeWithSettings(settings);
        };

        await bootstrap();

        indstillingerObserver.valueChanges.subscribe(() => {
            void bootstrap();
        });
    };
}
